import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Bitcoin,
  Copy,
  CheckCircle,
  Clock,
  Shield,
  ArrowLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
  Ship,
  Package,
  MapPin,
  Info,
  Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { calculateShippingCost, getDestinations } from '@/utils/shippingCalculator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback BTC rate if API fails
const BTC_USD_RATE_FALLBACK = 97500;

export default function CheckoutPage() {
  const { engineId } = useParams();
  const navigate = useNavigate();
  const [engine, setEngine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [btcRate, setBtcRate] = useState(BTC_USD_RATE_FALLBACK);
  const [btcPriceSource, setBtcPriceSource] = useState('loading');
  const [btcChange24h, setBtcChange24h] = useState(0);
  const [includeInsurance, setIncludeInsurance] = useState(true);
  const [btcAddress, setBtcAddress] = useState('');
  const [btcEnabled, setBtcEnabled] = useState(true);
  
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    shippingAddress: '',
    shippingCity: '',
    shippingRegion: 'North America',
    shippingCountry: '',
    notes: ''
  });

  const destinations = getDestinations();

  useEffect(() => {
    fetchEngine();
    fetchSettings();
    fetchBtcPrice();
  }, [engineId]);

  const fetchBtcPrice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/btc/price`);
      setBtcRate(response.data.price);
      setBtcPriceSource(response.data.source);
      setBtcChange24h(response.data.change_24h || 0);
    } catch (error) {
      console.error('Failed to fetch BTC price, using fallback');
      setBtcPriceSource('fallback');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setBtcAddress(response.data.btcWalletAddress || '');
      setBtcEnabled(response.data.btcEnabled !== false);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const fetchEngine = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/engines/${engineId}`);
      setEngine(response.data);
    } catch (error) {
      toast.error('Failed to load engine details');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  // Calculate shipping cost
  const shippingDetails = useMemo(() => {
    if (!engine) return null;
    return calculateShippingCost(engine, buyerInfo.shippingRegion, {
      insuranceIncluded: includeInsurance
    });
  }, [engine, buyerInfo.shippingRegion, includeInsurance]);

  // Calculate totals
  const orderTotals = useMemo(() => {
    if (!engine || !shippingDetails) return null;
    const enginePrice = engine.price || 0;
    const shippingCost = shippingDetails.total;
    const grandTotal = enginePrice + shippingCost;
    return {
      enginePrice,
      shippingCost,
      grandTotal,
      btcAmount: (grandTotal / btcRate).toFixed(8)
    };
  }, [engine, shippingDetails, btcRate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBuyerInfo(prev => ({ ...prev, [name]: value }));
  };

  const btcPaymentUri = orderTotals && btcAddress
    ? `bitcoin:${btcAddress}?amount=${orderTotals.btcAmount}&label=EngineShipParts&message=Engine-${engineId}`
    : '';

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success(`${type} copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleProceedToPayment = async () => {
    if (!buyerInfo.name || !buyerInfo.email || !buyerInfo.shippingAddress || !buyerInfo.shippingCountry) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!btcAddress) {
      toast.error('Bitcoin payments are not configured. Please contact support.');
      return;
    }

    setOrderSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/orders`, {
        engineId,
        buyerInfo: {
          ...buyerInfo,
          shippingDetails: shippingDetails
        },
        paymentMethod: 'btc',
        totalUsd: orderTotals.grandTotal,
        enginePrice: orderTotals.enginePrice,
        shippingCost: orderTotals.shippingCost,
        btcAmount: orderTotals.btcAmount,
        btcRate: btcRate,
        btcAddress: btcAddress
      });
      
      setOrderId(response.data.orderId);
      setStep(2);
      toast.success('Order created! Please complete BTC payment.');
    } catch (error) {
      toast.error('Failed to create order. Please try again.');
    } finally {
      setOrderSubmitting(false);
    }
  };

  const handleConfirmPayment = async () => {
    setOrderSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/orders/${orderId}/confirm-payment`, {
        txHash: ''
      });
      setStep(3);
      toast.success('Payment submitted! We will verify and confirm shortly.');
    } catch (error) {
      toast.error('Failed to confirm payment');
    } finally {
      setOrderSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </main>
    );
  }

  return (
    <main className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-slate-900 py-6">
        <div className="container-custom">
          <Link to={`/engine/${engineId}`} className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Engine Details
          </Link>
        </div>
      </div>

      <div className="container-custom py-12">
        {/* Progress Steps */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Your Details' },
              { num: 2, label: 'BTC Payment' },
              { num: 3, label: 'Confirmation' }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                  step >= s.num ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                </div>
                <span className={`ml-3 font-medium hidden sm:block ${
                  step >= s.num ? 'text-slate-900' : 'text-slate-400'
                }`}>{s.label}</span>
                {i < 2 && (
                  <div className={`hidden sm:block w-24 h-1 mx-4 rounded ${
                    step > s.num ? 'bg-orange-500' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Buyer Details */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Buyer Information Card */}
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Buyer Information</h2>
                  
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={buyerInfo.name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={buyerInfo.email}
                          onChange={handleInputChange}
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={buyerInfo.phone}
                          onChange={handleInputChange}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          value={buyerInfo.company}
                          onChange={handleInputChange}
                          placeholder="Marine Services LLC"
                        />
                      </div>
                    </div>

                    <div className="border-t border-slate-100 pt-6">
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-orange-500" />
                        Shipping Destination
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="shippingAddress">Street Address *</Label>
                          <Input
                            id="shippingAddress"
                            name="shippingAddress"
                            value={buyerInfo.shippingAddress}
                            onChange={handleInputChange}
                            placeholder="123 Port Street"
                            required
                          />
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="shippingCity">City</Label>
                            <Input
                              id="shippingCity"
                              name="shippingCity"
                              value={buyerInfo.shippingCity}
                              onChange={handleInputChange}
                              placeholder="Port City"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="shippingCountry">Country *</Label>
                            <Input
                              id="shippingCountry"
                              name="shippingCountry"
                              value={buyerInfo.shippingCountry}
                              onChange={handleInputChange}
                              placeholder="United States"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Region *</Label>
                            <Select 
                              value={buyerInfo.shippingRegion} 
                              onValueChange={(v) => setBuyerInfo(prev => ({...prev, shippingRegion: v}))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select region" />
                              </SelectTrigger>
                              <SelectContent>
                                {destinations.map(dest => (
                                  <SelectItem key={dest.name} value={dest.name}>
                                    {dest.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="notes">Special Instructions</Label>
                          <Input
                            id="notes"
                            name="notes"
                            value={buyerInfo.notes}
                            onChange={handleInputChange}
                            placeholder="Port name, delivery requirements, etc."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Calculator Card */}
                {shippingDetails && (
                  <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
                    <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                      <Ship className="w-6 h-6 text-orange-500" />
                      Shipping Estimate
                    </h2>

                    {/* Shipping Method */}
                    <div className="bg-slate-50 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{shippingDetails.containerType}</h4>
                          <p className="text-sm text-slate-600">{shippingDetails.methodDescription}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Transit: {shippingDetails.transitTime} days
                            </span>
                            <span>Weight: ~{shippingDetails.estimatedWeight.toLocaleString()} kg</span>
                            <span>Volume: ~{shippingDetails.estimatedCBM} CBM</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Cost Breakdown */}
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Base Ocean Freight</span>
                        <span className="font-mono">${shippingDetails.breakdown.baseFreight.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Fuel Surcharge (15%)</span>
                        <span className="font-mono">${shippingDetails.breakdown.fuelSurcharge.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Port Handling Fees</span>
                        <span className="font-mono">${shippingDetails.breakdown.portFees.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Documentation</span>
                        <span className="font-mono">${shippingDetails.breakdown.documentationFee.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeInsurance}
                            onChange={(e) => setIncludeInsurance(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-300 text-orange-500"
                          />
                          <span className="text-slate-600">Cargo Insurance (2%)</span>
                        </label>
                        <span className="font-mono">
                          {includeInsurance ? `$${shippingDetails.breakdown.insurance.toLocaleString()}` : '—'}
                        </span>
                      </div>
                      <div className="border-t border-slate-200 pt-3 flex justify-between font-semibold">
                        <span>Shipping Total</span>
                        <span className="text-orange-600">${shippingDetails.total.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Info Note */}
                    <div className="bg-blue-50 rounded-lg p-4 flex gap-3">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Ocean Freight from China</p>
                        <p className="text-blue-700">
                          Shipping is calculated based on {shippingDetails.method === 'LCL' ? 'Less than Container Load (LCL) rates' : 'Full Container Load (FCL) rates'}. 
                          Final shipping may vary based on actual dimensions and port conditions.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleProceedToPayment}
                  disabled={orderSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
                >
                  {orderSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Order...
                    </>
                  ) : (
                    <>
                      Proceed to Payment
                      <Bitcoin className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Step 2: BTC Payment */}
            {step === 2 && orderTotals && (
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Bitcoin className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Bitcoin Payment</h2>
                    <p className="text-slate-500">Order #{orderId}</p>
                  </div>
                </div>

                {/* Payment Amount */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-600">Total Amount Due</span>
                    <div className="flex items-center gap-2">
                      <RefreshCw 
                        className="w-4 h-4 text-slate-400 cursor-pointer hover:text-orange-500 transition-colors" 
                        title="Refresh rate" 
                        onClick={fetchBtcPrice}
                      />
                      <span className="text-xs text-slate-500">
                        1 BTC = ${btcRate.toLocaleString()}
                        {btcPriceSource === 'coingecko' && (
                          <span className="ml-1 text-green-600" title="Live price from CoinGecko">●</span>
                        )}
                        {btcChange24h !== 0 && (
                          <span className={`ml-1 ${btcChange24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ({btcChange24h >= 0 ? '+' : ''}{btcChange24h.toFixed(1)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-slate-900 mb-1">{orderTotals.btcAmount} BTC</div>
                    <div className="text-lg text-slate-600">${orderTotals.grandTotal.toLocaleString()} USD</div>
                    <div className="text-sm text-slate-500 mt-2">
                      (Engine: ${orderTotals.enginePrice.toLocaleString()} + Shipping: ${orderTotals.shippingCost.toLocaleString()})
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                <div className="flex justify-center mb-6">
                  <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                    <QRCode value={btcPaymentUri} size={200} />
                  </div>
                </div>

                {/* BTC Address */}
                <div className="space-y-4 mb-6">
                  <div>
                    <Label className="text-slate-500 text-sm">Send exactly this amount:</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        value={orderTotals.btcAmount} 
                        readOnly 
                        className="font-mono text-lg bg-slate-50"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(orderTotals.btcAmount, 'Amount')}
                        className={copied === 'Amount' ? 'border-green-500 text-green-600' : ''}
                      >
                        {copied === 'Amount' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-500 text-sm">To this Bitcoin address:</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        value={btcAddress} 
                        readOnly 
                        className="font-mono text-sm bg-slate-50"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(btcAddress, 'Address')}
                        className={copied === 'Address' ? 'border-green-500 text-green-600' : ''}
                      >
                        {copied === 'Address' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold mb-1">Important:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Send the exact BTC amount shown above</li>
                        <li>Payment must be completed within 30 minutes</li>
                        <li>Wait for network confirmation (1-3 confirmations)</li>
                        <li>Contact us if you have any issues</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleConfirmPayment}
                  disabled={orderSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                >
                  {orderSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      I&apos;ve Sent the Payment
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500 mt-4">
                  Having issues? <a href="tel:+12012344851" className="text-orange-600 hover:underline">Contact Support</a>
                </p>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && orderTotals && (
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Payment Submitted!</h2>
                <p className="text-slate-600 mb-6">
                  Thank you for your order. We are verifying your Bitcoin payment.
                  You will receive a confirmation email at <strong>{buyerInfo.email}</strong> once verified.
                  Once your payment is confirmed, you can follow your shipment on the tracking page below.
                </p>
                
                <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
                  <h3 className="font-semibold text-slate-900 mb-3">Order Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Order ID:</span>
                      <span className="font-mono">{orderId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Engine:</span>
                      <span>{engine?.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Engine Price:</span>
                      <span>${orderTotals.enginePrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Shipping:</span>
                      <span>${orderTotals.shippingCost.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t border-slate-200">
                      <span>Total Amount:</span>
                      <span>{orderTotals.btcAmount} BTC (${orderTotals.grandTotal.toLocaleString()})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status:</span>
                      <span className="text-amber-600 font-medium">Pending Verification</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap justify-center gap-4">
                  <Button asChild className="bg-orange-500 hover:bg-orange-600" data-testid="checkout-track-order-btn">
                    <Link to={`/track?orderId=${orderId}`} state={{ email: buyerInfo.email }}>
                      <Truck className="w-4 h-4 mr-2" />
                      Track Your Order
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/inventory">
                      Continue Shopping
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/contact">
                      Contact Support
                    </Link>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 sticky top-24">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Order Summary</h3>
              
              {engine && (
                <>
                  <div className="aspect-video rounded-lg overflow-hidden mb-4">
                    <img 
                      src={engine.images?.[0]} 
                      alt={engine.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <h4 className="font-semibold text-slate-900 mb-1 line-clamp-2">{engine.title}</h4>
                  <p className="text-sm text-slate-500 mb-4">{engine.brand} • {engine.model}</p>
                  
                  <div className="border-t border-slate-100 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Condition</span>
                      <span className="text-slate-900">{engine.condition}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Horsepower</span>
                      <span className="text-slate-900">{engine.horsepower?.toLocaleString()} HP</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Hours</span>
                      <span className="text-slate-900">{engine.hours || 'N/A'}</span>
                    </div>
                  </div>
                  
                  {orderTotals && (
                    <div className="border-t border-slate-100 mt-4 pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-500">Engine Price</span>
                        <span className="text-slate-900">${orderTotals.enginePrice.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-slate-500 flex items-center gap-1">
                          <Truck className="w-3 h-3" />
                          Shipping ({buyerInfo.shippingRegion})
                        </span>
                        <span className="text-slate-900">${orderTotals.shippingCost.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-slate-200">
                        <span>Grand Total</span>
                        <span className="text-orange-600">${orderTotals.grandTotal.toLocaleString()}</span>
                      </div>
                      <div className="text-right text-sm text-slate-500">
                        ≈ {orderTotals.btcAmount} BTC
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Trust Badges */}
              <div className="border-t border-slate-100 mt-6 pt-6 space-y-3">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Shield className="w-4 h-4 text-green-500" />
                  Secure BTC Transaction
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Ship className="w-4 h-4 text-blue-500" />
                  Worldwide Ocean Freight
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-orange-500" />
                  24/7 Support Available
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
